import ApiService from '.';
import { AddMessageBoardParams, AddOrRemoveAssingeeParams } from '../params/redux/messageboard';

/**
 * @heading1: Message Board API service.
 * @paragraph: This module provides a set of methods to interact with the Message Board API.
 * It handles operations like Fetching Message board, create conversations, view conversations etc.
 */

const MessageBoardApi = {
    /**
     * @heading3: Fetch MessageBoard
     * @paragraph: Sends a POST request to retrieve all MessageBoards available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The client_id required for getting related MessageBoards.
     * @returns: {Promise<unknown[]>} - The response containing a details of MessageBoard.
     * @separator:
     */
    fetchMessageBoard: (token: string, client_id: number) => {
        // Perform a POST request to fetch MessageBoard
        return ApiService.apiHit({
            url: 'get-message-board',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Create MessageBoard
     * @paragraph: Sends a POST request to create MessageBoard for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The client_id required for Creating.
     * @param: {string} name - The name required for Creating.
     * @returns: {Promise<unknown[]>} - The response containing a details of MessageBoard.
     * @separator:
     */
    createMessageBoard: (token: string, data: AddMessageBoardParams) => {
        // Perform a POST request to create MessageBoard
        return ApiService.apiHit({
            url: 'create-message-board',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: create MessageBoard conversation
     * @paragraph: Sends a POST request to create a conversation.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {number} messageboard_id - The data containing the watercooler id for the operation.
     * @param: {string} message - The data containing the message for the operation.
     * @param: {string} file - The data containing the file for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    createMessageBoardConversation: (token: string, message_board_id: number, message: string, file: string) => {
        // Perform a Post request to create client MessageBoard conversation endpoint with the token
        return ApiService.apiHit({
            url: 'create-message-board-conversation',
            method: 'post',
            token,
            data: { message_board_id, message, file },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: fetch Single MessageBoard conversation
     * @paragraph: Sends a POST request to fetch a detail of Message Board.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {number} messageboard_id - The data containing the watercooler id for the operation.
     * @param: {string} message - The data containing the message for the operation.
     * @param: {string} file - The data containing the file for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    fetchMessageBoardDetail: (token: string, id: number) => {
        // Perform a Post request to create client MessageBoard conversation endpoint with the token
        return ApiService.apiHit({
            url: 'get-single-message-board',
            method: 'post',
            token,
            data: { id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Add or remove assignee MessageBoard conversation
     * @paragraph: Sends a POST request to fetch a detail of Message Board.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {number} messageboard_id - The data containing the watercooler id for the operation.
     * @param: {string} message - The data containing the message for the operation.
     * @param: {string} file - The data containing the file for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    addOrRemoveAssigneeOfMessageBoard: (token: string, data: AddOrRemoveAssingeeParams) => {
        // Perform a Post request to create client MessageBoard conversation endpoint with the token
        return ApiService.apiHit({
            url: 'add-remove-messageboard-assignee',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default MessageBoardApi;
